import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import {
    Button,
    Box,
    Typography,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    FormControlLabel,
    TextField,
    Tooltip,
    Stack,
    Chip,
    Tabs, 
    Tab,
    Divider,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress
} from "@mui/material";
import axios from "axios";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PdfViewerDialog from '../../../PdfViewerDialog'; 
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import dayjs from 'dayjs';
import { AuthContext } from "../../../../helpers/AuthContext";
import CustomDatePicker from "../../../CustomDatePicker";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import styles from './index.module.css'
import { 
    updatePurchaseOrderDetails, 
    sendMailToSupplier, 
    previewPurchaseOrderPdf,
    deletePurchaseOrderApi, 
    addProductionNoteByPO, 
    invalidatePurchaseOrderCache,
    publishPurchaseOrderInvalidation  
} from '../../../../services/purchaseOrder';
import CustomDropdown from "../../../CustomDropdownWithPlus";
import FloatingLoader from "../../../FloatingLoader";
import CustomDropDownColor from "../../../CustomDropDownColor";
import { useInstanceNextActionTypes } from "../../../../helpers/instanceTerms";
import EmailComposer from "../../../EmailComposer";
import useWorkflowDisplaySettings from "../../../../hooks/useWorkflowDisplaySettings";
import { calculatePOFinalTotals } from "../../shared/poTotals";
import { seedArtworkAttachmentSelection } from "../../../../utils/purchaseOrderEmailAttachments";
import { recipientFieldsFromPurchaseOrder } from "../../../../utils/purchaseOrderEmailRecipients";
import { isDecoratorPo, resolvePoRoutingDisplay } from "../../shared/poRouting";
import PurchaseOrderNumberCell from "./PurchaseOrderNumberCell";
import { buildPurchaseOrderSupplierEmailSubject } from "./purchaseOrderEmailSubject";
import { canMutatePurchaseOrder, createPoMutationGuard, getRoleList } from './poReadOnly';
import { PURCHASE_ORDER_STATUS_OPTIONS } from '../../../../constants/purchaseOrderStatuses';
function extractKey(units, key) {
    return units.map((unit) => {
        return { [key]: unit[key] };
    });
}
const IMAGE_CDN = `https://api.asicentral.com/v1/`
const purchaseOrderStatusOptions = PURCHASE_ORDER_STATUS_OPTIONS;

const isPdfUrl = (s = '') => /\.pdf(?:$|[?#])/i.test(s);

// ---------- COST HELPERS (prefer supplier/net cost over retail) ----------
const toNum = (v) => Number.parseFloat(v) || 0;
const firstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null);

// Charges (deco/service) — cost side
const getChargeQty = (c) => toNum(firstDefined(c?.quantity, c?.Quantity, c?.qty, 1));
const getChargeUnitCost = (c) =>
  toNum(
    firstDefined(
      c?.unitCost, c?.UnitCost,
      c?.cost, c?.Cost,
      c?.supplierCost, c?.SupplierCost,
      c?.netCost, c?.NetCost,
      0
    )
  );

// Items — cost side
const getItemQty = (it) => toNum(firstDefined(it?.Quantity, it?.quantity, it?.Qty, 1));
const getItemUnitNet = (it) =>
  toNum(
    firstDefined(
      it?.netCost, it?.NetCost,                 // prefer explicit net
      it?.supplierNetCost, it?.SupplierNetCost, // supplier net
      it?.supplierCost, it?.SupplierCost,       // supplier unit cost
      // never fall back to generic Cost/cost
      it?.pricings?.[0]?.NetCost,               // last-ditch fallback
      0
    )
  );
const getItemTotalNet = (it) => {
  const pricingRows = Array.isArray(it?.pricings) ? it.pricings : [];
  if (pricingRows.length > 0) {
    return pricingRows.reduce((sum, p) => {
      const unit = toNum(firstDefined(p?.NetCost, p?.netCost, p?.supplierNetCost, p?.SupplierNetCost, p?.supplierCost, p?.SupplierCost, 0));
      const qty = toNum(firstDefined(p?.Quantity, p?.quantity, 0));
      return sum + (unit * qty);
    }, 0);
  }
  return getItemUnitNet(it) * getItemQty(it);
};

// helper: does this charge have any cost-side fields?
const hasAnyCostField = (c) => (
  c?.unitCost != null || c?.UnitCost != null ||
  c?.cost != null || c?.Cost != null ||
  c?.supplierCost != null || c?.SupplierCost != null ||
  c?.netCost != null || c?.NetCost != null ||
  c?.costTotal != null || c?.CostTotal != null ||
  c?.totalCost != null || c?.TotalCost != null ||
  c?.netTotal  != null || c?.NetTotal  != null 
);

// keep your broadened getChargeTotalCost
const getChargeTotalCost = (c) => {
  const explicit = (
    c?.costTotal ?? c?.CostTotal ?? c?.totalCost ?? c?.TotalCost ??
    c?.netTotal  ?? c?.NetTotal  
  );
  const n = Number(explicit);
  if (Number.isFinite(n) && n >= 0) return n;

  const unit =
    toNum(
      firstDefined(
        c?.unitCost, c?.UnitCost,
        c?.cost, c?.Cost,
        c?.supplierCost, c?.SupplierCost,
        c?.netCost, c?.NetCost,
        // DO NOT fall back to price/retail for cost preview
        0
      )
    );
  const qty = getChargeQty(c);
  return unit * qty;
};

// replace sumChargeCosts with this
const sumChargeCosts = (decosOrCharges = []) => {
  const charges = decosOrCharges.flatMap((x) => {
    if (!x) return [];
    if (Array.isArray(x?.Charge)) return x.Charge;
    return [x];
  })
  // ✅ ignore retail-only lines (no cost-side signals)
  .filter(hasAnyCostField);

  // (optional) de-dupe identical charges if upstream can double-insert
  const key = (c) => [
    c?.id ?? c?.otherServiceId ?? "",
    String(c?.title ?? c?.Name ?? "").toLowerCase().trim(),
    toNum(c?.unitCost ?? c?.UnitCost ?? c?.cost ?? c?.Cost ?? c?.supplierCost ?? c?.SupplierCost ?? c?.netCost ?? c?.NetCost ?? 0),
    getChargeQty(c),
    toNum(c?.costTotal ?? c?.CostTotal ?? c?.totalCost ?? c?.TotalCost ?? c?.netTotal ?? c?.NetTotal ?? c?.total ?? c?.Total ?? -1)
  ].join("|");

  const map = new Map();
  charges.forEach((c) => map.set(key(c), c));
  const uniq = [...map.values()];

  return uniq.reduce((sum, c) => sum + getChargeTotalCost(c), 0);
};

// (small robustness tweak) normalization can also accept "Charges"
const normalizeDecoForCost = (d) => ({
  ...d,
  Charge: (() => {
    const raw = d?.Charge ?? d?.Charges ?? [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") { try { const a = JSON.parse(raw); return Array.isArray(a) ? a : []; } catch { return []; } }
    return [];
  })(),
  DecoratorDetails:
    typeof d?.DecoratorDetails === "string"
      ? (d.DecoratorDetails ? (() => { try { return JSON.parse(d.DecoratorDetails); } catch { return {}; } })() : {})
      : (d?.DecoratorDetails || {}),
});


const detectCarrier = (tn = "") => {
    const s = String(tn).trim();
    if (/^1Z[0-9A-Z]{16}$/i.test(s)) return "UPS";
    if (/^\d{12}$|^\d{15}$|^\d{20}$/.test(s)) return "FedEx";
    if (/^(94|93|92|95|94)\d{20,22}$/.test(s) || /^420\d{5}\d+/.test(s)) return "USPS";
    if (/^\d{16}$|^\d{22}$/.test(s)) return "Canada Post"; // rough
    return "Unknown";
  };
  
  const trackingUrl = (tn) => {
    const c = detectCarrier(tn);
    switch (c) {
      case "UPS":         return `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(tn)}`;
      case "FedEx":       return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tn)}`;
      case "USPS":        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(tn)}`;
      case "Canada Post": return `https://www.canadapost-postescanada.ca/track-reperage/en#/resultList?searchFor=${encodeURIComponent(tn)}`;
      default:            return `https://www.google.com/search?q=${encodeURIComponent(tn + " tracking")}`;
    }
  };

  const fmtTs = (ts) => {
    if (!ts) return "—";
    // accept Date, ISO string, or number
    return dayjs(ts).isValid() ? dayjs(ts).format("MMM D, YYYY h:mm A") : "—";
  };
  
  const pickDisplayPdf = (pdfs = []) => {
    if (!Array.isArray(pdfs) || pdfs.length === 0) return null;
    const active = pdfs.find(p => p?.active && p?.url);
    if (active) return active;
  
    // fallback: most recent by storedAt (if present) or just the last
    const sorted = [...pdfs].filter(p => p?.url);
    sorted.sort((a, b) => new Date(a.storedAt || 0) - new Date(b.storedAt || 0));
    return sorted[sorted.length - 1] || null;
  };
  

const statusDescriptions = {
    Open:          "PO is active and in progress.",
    "Follow Up":   "Action needed — please follow up with the supplier.",
    Created:        "PO record has been created but not yet sent to the supplier.",
    Submitted:      "PO has been submitted to the supplier for processing.",
    Confirmed:      "Supplier has acknowledged and confirmed this PO.",
    "Production Scheduled": "Linked decorator work is scheduled; production has not started.",
    "Production Complete": "Supplier/decorator production is complete but the PO has not necessarily shipped.",
    Shipped:        "Goods have been shipped by the supplier.",
    Received:       "Goods have been received from the supplier.",
    "In Progress":    "Order is actively moving through post-receipt handling.",
    "Partially Received": "Order has been partially received from supplier/production.",
    "Ready For Billing": "Supplier confirms goods have shipped and invoice can be issued.",
    Closed:         "PO workflow completed—no further action required.",
    Billed:         "Invoice has been generated and sent to the client for payment.",
};

const statusColors = {
    Created:        "#cfffcf",
    Open:           "#e6f4ff",   
    "Follow Up":    "#fff4cc",
    Submitted:      "#fcf3cf",
    Confirmed:      "#cfeffc",
    "Production Scheduled": "#e8eaf6",
    "Production Complete": "#ede7f6",
    Shipped:        "#fcefcf",
    Received:       "#d6f5ff", 
    "In Progress":   "#fff2cc", 
    "Partially Received": "#ffe0b2", 
    "Ready For Billing": "#cfcfff",
    Closed:         "#e0e0e0",
    Billed:         "#ffcfcf",
};

  const supplierConversationStatusDescriptions = {
    OK:        "Supplier has no issues and confirms everything is on track.",
    Problem:   "Supplier reported a problem that needs to be addressed.",
    "Follow Up": "Awaiting additional details or clarification from the supplier.",
  };
  const proofStatusDescriptions = {
    "No Proof":      "No artwork proof is required for this PO.",
    "Proof Needed":  "An artwork proof is required. Notify designer and order/client rep to begin.",
    "Uploaded": "Proof files have been uploaded and are still awaiting final approval.",
    "Approved": "Artwork has been approved; production has not yet marked proofing complete.",
    "Proof Complete": "Purchasing has manually marked the proofing work for this PO as complete.",
  };
const poStatusIndicatorOptions = [
    { id: "OK", name: "OK" },
    { id: "Problem", name: "Problem" },
    { id: "Follow Up", name: "Follow Up" },
];

const buildProofRequirementOptions = (row = {}) => [
    { id: "No Proof", name: "No Proof" },
    { id: "Proof Needed", name: "Proof Needed" },
    { id: "Uploaded", name: "Proof Uploaded" },
    // Display an inherited approval, but do not let users grant approval manually.
    { id: "Approved", name: "Approved", disabled: true },
    {
      id: "Proof Complete",
      name: "Proof Complete",
      disabled: row?.proofStatus !== "Proof Complete" && row?.canMarkProofComplete !== true,
    },
];

/* Purchase order colour system */
  
  // similarly, for supplier-conversation
  const convoColors = {
    OK:        "#d4f5d4",
    Problem:   "#f5d4d4",
    "Follow Up":"#f5f1d4",
  };
  
  // and proofs
  const proofColors = {
    "No Proof":     "#e8ffe8",
    "Proof Needed": "#fff0e8",
    "Approved": "#28694D",
    "Proof Complete": "#6B3FA0",
  };

/* Build image URLs */

// strip leading slashes, prepend CDN if needed, append size
export function buildImageUrl(rawPath, size = "normal") {
    if (!rawPath) return ""; 
    const path = rawPath.replace(/^\/+/, "");
    // already absolute?
    if (/^https?:\/\//.test(path)) {
      const [base] = path.split(/\?size=/);
      return `${base}?size=${size}`;
    }
    // relative → CDN
    const base = IMAGE_CDN.replace(/\/+$/, "");
    return `${base}/${path}?size=${size}`;
  }

const transformData = (apiResponse) => {
    console.log(apiResponse, "apiResponse");
    return (
        apiResponse?.map((product) => {
            return {
                id: product.ProductId,
                supplier: `${product?.SupplierName} ( ${product?.CurrencyCode || "CAD"
                    } )`,
                name: product.Name,
                image: product.ImageUrl,
                description: product.Description,
                shortDescription: product.ShortDescription,
                data: [
                    {
                        item: "TBD/TBD",
                        unit: product.Quantity || 0,
                        margin: product.Margin ? `${product.Margin}%` : "0%",
                        tax: "",
                        amount: product.Subtotal || 0,
                    },
                    {
                        item: "Total Units",
                        unit: product.Quantity || 0,
                        margin: "",
                        tax: "",
                        amount: "",
                    },
                    {
                        item: `Total (${product?.CurrencyCode})`,
                        unit: "",
                        tax: "GST",
                        margin: product.Margin ? `${product.Margin}%` : "0%",
                        amount: product.Subtotal || 0,
                    },
                ],
                price: product.Subtotal,
                quantity: product?.Quantity,
            };
        }) || []
    );
};

const broadcastProductionNote = (note) => {
    window.dispatchEvent(
      new CustomEvent("production-note-added", { detail: note })
    );
  };

const SelectedProductsPopup = ({ open, setSelectedPOList, setEstimationPopup, requestProofs, fetchPurchaseOrders, order, productionRepOption, purchaseOrders, loading, setLoading, showPoUrl = false, showPoNotesAction = true, poProofsPageUrl = "" }) => {
    // const transformedData = transformData(order?.Products);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [openNotesDialog, setOpenNotesDialog] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [nextActionDate, setNextActionDate] = useState(null);
    const [value, setValue] = useState("");
    const [hoveredSupplierStatus, setHoveredSupplierStatus] = useState(null);
    const [hoveredSupplierStatusRow, setHoveredSupplierStatusRow] = useState(null);
    const [hoveredConversationStatus, setHoveredConversationStatus] = useState(null);
    const [hoveredProofStatus, setHoveredProofStatus] = useState(null);
    const [hoveredProofStatusRow, setHoveredProofStatusRow] = useState(null);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailRow, setEmailRow] = useState(null);
    // PDFs
    const [pdfHistoryOpen, setPdfHistoryOpen] = useState(false);
    const [pdfHistoryList, setPdfHistoryList] = useState([]);
    const [pdfHistoryTitle, setPdfHistoryTitle] = useState("");
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [deletedPoIds, setDeletedPoIds] = useState(() => new Set());

    const openEmailDialog = (row) => {
      if (blockPoMutation({ action: 'email-supplier' })) return;
      if (!row?.productionRep) return;
      setEmailRow(row);
      setEmailOpen(true);
    };
    let { instanceUniqueId, orderId } = useParams();
    const getTrackingDisplay = (po = {}) => {
      const raw = po?.trackingNumbers;
      const list = Array.isArray(raw)
        ? raw.map((s) => String(s || "").trim()).filter(Boolean)
        : String(raw || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
      return list.length ? list.join(", ") : "";
    };
    const formatShippedDate = (value) => {
      if (!value) return "";
      const parsed = dayjs(value);
      if (!parsed.isValid()) return String(value);
      return parsed.format("MMM D, YYYY");
    };
    const formatShippingCost = (value, currency = "CAD") => {
      const cents = Number(value);
      if (!Number.isFinite(cents) || cents <= 0) return "";
      return (cents / 100).toLocaleString(undefined, {
        style: "currency",
        currency: currency || "CAD",
      });
    };
    const { nextActionTypeOptions } = useInstanceNextActionTypes(instanceUniqueId);

    const { getWorkflowLabel, getWorkflowTooltip } = useWorkflowDisplaySettings(instanceUniqueId);
    const workflowPurchaseOrderStatusOptions = useMemo(
      () => purchaseOrderStatusOptions.map((opt) => ({
        ...opt,
        name: getWorkflowLabel("PO", opt.id),
      })),
      [getWorkflowLabel]
    );

    const { authState } = useContext(AuthContext);
    const currentUser = authState?.user || authState || {};
    const roleList = getRoleList(currentUser);
    const isAdministrator = roleList.some((role) => ["administrator", "admin", "super-administrator", "superadmin"].includes(String(role).trim().toLowerCase().replace(/[_\s]+/g, "-")));
    const blockPoMutation = createPoMutationGuard(
      currentUser,
      (message) => toast.error(message, { id: 'po-sales-rep-read-only' })
    );
    const canEditSupplierStatus = canMutatePurchaseOrder(currentUser, { action: 'edit-po-field', fieldName: 'supplierStatus' });
    const canEditConversationStatus = canMutatePurchaseOrder(currentUser, { action: 'edit-po-field', fieldName: 'productionRepStatus' });
    const canEditProofStatus = canMutatePurchaseOrder(currentUser, { action: 'manage-proofs' });
    const canAssignPurchasingRep = canMutatePurchaseOrder(currentUser, { action: 'assign-purchasing-rep' });
    const canEmailSupplier = canMutatePurchaseOrder(currentUser, { action: 'email-supplier' });
    const canDeletePurchaseOrder = canMutatePurchaseOrder(currentUser, { action: 'delete-purchase-order' });
    const canEditNextAction = canMutatePurchaseOrder(currentUser, { action: 'edit-po-field', fieldName: 'nextAction' });
    const canEditNextActionDate = canMutatePurchaseOrder(currentUser, { action: 'edit-po-field', fieldName: 'nextActionDate' });
    const handleCheckboxChange = (index) => {
        const updatedSelectedProducts = [...selectedProducts];
        if (updatedSelectedProducts.includes(index)) {
            updatedSelectedProducts.splice(updatedSelectedProducts.indexOf(index), 1);
        } else {
            updatedSelectedProducts.push(index);
        }
        setSelectedProducts(updatedSelectedProducts);
        const selectedPOIds = updatedSelectedProducts
            .map(i => purchaseOrderList[i]?.poId)
            .filter(Boolean); // remove undefined/null if any

        setSelectedPOList(selectedPOIds);

        // setSelectedPOList(updatedSelectedProducts);
    };


    useEffect(() => {
        if (order?.Products && order?.Products?.length > 0) {
            const initialSelected = order?.Products?.map((_, index) => index);
            setSelectedProducts(initialSelected);
        }
    }, [order?.Products]);

    useEffect(() => {
        const fetchTemplates = async () => {
            if (!instanceUniqueId) return;
            try {
                const headers = { headers: { accessToken: localStorage.getItem('accessToken') } };
                const instanceRes = await axios.get(
                    `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`,
                    headers
                );
                const instanceId = instanceRes.data?.id;
                if (!instanceId) return;
                const templatesRes = await axios.get(
                    `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/email-templates`,
                    headers
                );
                const templates = templatesRes.data?.templates || [];
                setEmailTemplates(templates.filter((template) => template.enabled !== false));
            } catch (error) {
                console.warn('Failed to load email templates', error);
            }
        };

        fetchTemplates();
    }, [instanceUniqueId]);

    const handleEmailToSupplier = async (id) => {
        if (blockPoMutation({ action: 'email-supplier' })) return;
        if (!id) {
            toast.error('Purchase Order ID is missing');
            return;
        }
        try {
            setLoading(true);
            await sendMailToSupplier(id);
            toast.success('Email successfully sent to the supplier');
            setLoading(false);
        } catch (error) {
            console.error(error);
            toast.error('Failed to send email to supplier');
            setLoading(false);
        }
    }
    const deletePurchaseOrder = async (id) => {
        if (blockPoMutation({ action: 'delete-purchase-order' })) return;
        if (!id) {
            toast.error('Purchase Order ID is missing');
            return;
        }
        const poIdKey = String(id);
        setDeletedPoIds((prev) => {
            const next = new Set(prev);
            next.add(poIdKey);
            return next;
        });

        try {
            setLoading(true);
            await deletePurchaseOrderApi(id);
            invalidatePurchaseOrderCache(id);
            publishPurchaseOrderInvalidation(id);
            await fetchPurchaseOrders();
            toast.success(`PO #${id} archived`);
        } catch (error) {
            console.error(error);
            setDeletedPoIds((prev) => {
                const next = new Set(prev);
                next.delete(poIdKey);
                return next;
            });
            toast.error('Failed to archive Purchase Order');
        } finally {
            setLoading(false);
        }
    }

    const purchaseOrderList = useMemo(() => {
        if (!Array.isArray(purchaseOrders?.purchaseOrders)) return [];
        const filteredRows = purchaseOrders.purchaseOrders.filter(
          (po) => !deletedPoIds.has(String(po?.poId))
        );
      
        const toNum = (v) => Number.parseFloat(v) || 0;
      
        const safeParse = (v, d = {}) => {
          if (!v) return d;
          if (typeof v === 'string') {
            try { return JSON.parse(v); } catch { return d; }
          }
          return v;
        };

        // --- In-house Elite detection (decorator 4 ↔ supplier 2147483647) ---
        const ELITE_DECORATOR_ID = "4";
        const ELITE_SUPPLIER_ID  = "2147483647";
        const eliteNameRx = /elite\s+(awards|decorations|art\s+department)/i;



                // === Long-lived In-House Profiles (extensible) =======================
                // Add new in-house departments by appending entries here.
                // Matching is ANY-of criteria (ids, names, email domains).
                // Policies:
                //  - decoratorPOCost:
                //      'charges_only'         -> only external decoration charges
                //      'fold_base_and_all'    -> baseNet  inline  external (in-house fold)
                //      'external_only'        -> (default) only external charges (3P decorators)
                //  - uiTotalMode:
                //      'sell_with_tax' | 'cost_preview'
                const INHOUSE_PROFILES = [
                  {
                    key: 'elite-awards',
                    match: {
                      names:  [/elite\s+awards\s+department/i],
                      emails: [/@elitedm\.ca$/i],
                    },
                    policies: {
                      decoratorPOCost: 'charges_only',
                      uiTotalMode: 'sell_with_tax',
                    }
                  },
                  {
                    key: 'elite-general',
                    match: {
                      decoratorIds: [ELITE_DECORATOR_ID],
                      supplierIds:  [ELITE_SUPPLIER_ID],
                      vendorIds:    [ELITE_SUPPLIER_ID],
                      names:        [/elite\s+decorations/i, /elite\s+art\s+department/i],
                    },
                    policies: {
                      decoratorPOCost: 'fold_base_and_all',
                      uiTotalMode: 'cost_preview',
                    }
                  },
                ];
        
                const matchInhouseProfile = (poObj) => {
                  const si    = safeParse(poObj?.supplierInfo, {});
                  const name  = String(si?.supplierName || poObj?.supplierName || "");
                  const email = String(si?.supplierEmail || poObj?.supplierEmail || "");
                  const decoId     = String(si?.decoratorId ?? "");
                  const supplierId = String(si?.supplierId   ?? "");
                  const vendorSid  = String(si?.vendorSupplierId ?? "");
        
                  const any = (arr, pred) => Array.isArray(arr) && arr.some(pred);
                  return INHOUSE_PROFILES.find(p => {
                    const m = p.match || {};
                    return (
                      any(m.decoratorIds, id => id === decoId) ||
                      any(m.supplierIds,  id => id === supplierId) ||
                      any(m.vendorIds,    id => id === vendorSid) ||
                      any(m.names,        rx => rx.test(name)) ||
                      any(m.emails,       rx => rx.test(email))
                    );
                  }) || null;
                };
        
                // Backward-compatible boolean (used elsewhere in memo)
                const isInhouseElitePO = (poObj) => !!matchInhouseProfile(poObj);

      
        // normalize getters for service lines (all casings / shapes)
        const getQty = (c) => toNum(c?.quantity ?? c?.Quantity ?? c?.qty ?? 1);
        const getUnit = (c) =>
          toNum(
            c?.unitCost ?? c?.UnitCost ??
            c?.retailPrice ?? c?.RetailPrice ??
            c?.Retail ?? c?.price ?? 0
          );
        const getTotal = (c) => {
          const raw = c?.total ?? c?.Total;
          const unit = getUnit(c);
          const qty  = getQty(c);
          const computed = unit * qty;
          const n = Number(raw);
          return Number.isFinite(n) && n > 0 ? n : computed;
        };
        const getTitle = (c) => String(c?.title ?? c?.Name ?? c?.item ?? 'Service');
      
        const chargeKey = (c) => [
          c?.otherServiceId ?? c?.id ?? '',
          getTitle(c).toLowerCase(),
          getUnit(c),
          getQty(c),
          getTotal(c),
        ].join('|');
      
        const sumPricings = (item) =>
          (item.pricings || []).reduce((s, p) => s + toNum(p.Total), 0);
    
        const sumCharges = (decos = []) =>
        decos
            .flatMap(d => {
            // decoration.Charge can be array or stringified array
            const raw = d?.Charge ?? [];
            if (Array.isArray(raw)) return raw;
            if (typeof raw === 'string') {
                try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr : []; } catch { return []; }
            }
            return [];
            })
            .reduce((s, c) => s + getTotal(c), 0);   // <-- use computed total, not just c.Total

      
        const isEliteName = (name = "") =>
          /elite\s+decorations|elite\s+art\s+department/i.test(name);
      
        const splitDecos = (item, po) => {
          const all = item.decorations || [];
          const poDecoratorId = String(po?.supplierInfo?.decoratorId || "");
          return {
            inline:   all.filter(d => !d?.DecoratorDetails?.decoratorId),
            external: all.filter(d => String(d?.DecoratorDetails?.decoratorId) === poDecoratorId),
          };
        };
      
        const itemTotalForPO = (po, item) => {
          const type = po.purchaseOrderType;
          const { inline, external } = splitDecos(item, po);
      
          const isDecoratorPO =
            !!po?.supplierInfo?.decoratorId ||
            isEliteName(po?.supplierInfo?.supplierName) ||
            external.some(d => String(d?.DecoratorDetails?.source).toLowerCase() === "elite");
      
          if (isDecoratorPO) return sumCharges(external);
      
          if (type === "No_decoration")                         return sumPricings(item);
          if (type === "Same_supplier_decoration")              return sumPricings(item) + sumCharges(inline);
          if (type === "Different_supplier_decoration")         return sumCharges(external);
          if (type === "Combined_supplier_and_external_decoration")
                                                                return sumPricings(item) + sumCharges(inline) + sumCharges(external);
          return sumPricings(item) + sumCharges(inline) + sumCharges(external);
        };
      
        const gatherServiceCharges = (po) => {
          const out = [];
          const pushMaybe = (val) => {
            if (Array.isArray(val)) out.push(...val);
            else if (typeof val === 'string') {
              try { const arr = JSON.parse(val); if (Array.isArray(arr)) out.push(...arr); } catch {}
            }
          };
      
          // top-level
          pushMaybe(po?.charges);
          pushMaybe(po?.otherService);
          pushMaybe(po?.otherServices);
      
          // supplierInfo (string or object)
          const si = safeParse(po?.supplierInfo, {});
          pushMaybe(si?.charges);
          pushMaybe(si?.supplierCharges);
          pushMaybe(si?.serviceCharges);
          pushMaybe(si?.otherService);
          pushMaybe(si?.otherServices);
      
          // per-item (in case any got stashed there)
          (po?.items || []).forEach((it) => {
            const src = safeParse(it, it);
            pushMaybe(src?.charges);
            pushMaybe(src?.Charges);
            pushMaybe(src?.Charge);
            pushMaybe(src?.otherService);
            pushMaybe(src?.otherServices);
          });
      
          // de-dup
          const m = new Map();
          out.forEach(c => m.set(chargeKey(c), c));
          return [...m.values()];
        };

        const numberOrNull = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        };

        const withDisplayTotals = (row) => {
          const candidates = [
            { key: 'sharedFinalTotal', value: numberOrNull(row?.sharedFinalTotal) },
            { key: 'finalOrderTotal', value: numberOrNull(row?.finalOrderTotal) },
            { key: 'orderTotalWithTax', value: numberOrNull(row?.orderTotalWithTax) },
            { key: 'computedTotalWithTax', value: numberOrNull(row?.computedTotalWithTax) },
            {
              key: 'computedTotal+tax',
              value: (() => {
                const sub = numberOrNull(row?.computedTotal);
                const tax = numberOrNull(row?.tax);
                return sub != null && tax != null ? sub + tax : null;
              })(),
            },
            {
              key: 'previewSubtotal+previewTax',
              value: (() => {
                const sub = numberOrNull(row?.previewSubtotal);
                const tax = numberOrNull(row?.previewTax);
                return sub != null && tax != null ? sub + tax : null;
              })(),
            },
            { key: 'previewTotal', value: numberOrNull(row?.previewTotal) },
          ];

          const picked = candidates.find((c) => c.value != null);
          return {
            ...row,
            displayTotal: picked?.value ?? 0,
            displayTotalSource: picked?.key || 'fallback_zero',
          };
        };

        return filteredRows.map((po) => {
          // ensure supplierInfo is an object for downstream checks
          const poSI = safeParse(po?.supplierInfo, {});
          const rr = po?.resolvedRecipients || {}; // ✅ carry through auto routing
          const inhouseProfile = matchInhouseProfile(po);
          const decoratorParty = isDecoratorPo({ ...po, supplierInfo: poSI });
          const partyEmailCandidate = decoratorParty
            ? (poSI?.decoratorEmail || poSI?.DecoratorEmail || poSI?.supplierEmail)
            : (poSI?.supplierEmail || poSI?.SupplierEmail);
          const partyEmail = String(partyEmailCandidate || '').trim().toLowerCase() ===
            String(po.clientContactEmail || '').trim().toLowerCase()
            ? ''
            : partyEmailCandidate || '';

          const baseRow = {
            poId: po.id,
            poNumber: po.poNumber,
            supplierStatus: po.supplierStatus,
            productionRep: po.productionRep,
            proofStatus: po.proofStatus,
            productionRepStatus: po.productionRepStatus,
            supplierName: poSI?.supplierName || po?.supplierInfo?.supplierName || "",
            supplierEmail: partyEmail,
            orderId: po.orderId,
            clientContactEmail: po.clientContactEmail,
            nextAction: po.nextAction,
            productionRepDetails: po.productionRepDetails,
            nextActionDate: dayjs(po.nextActionDate),
            trackingNumbers: po.trackingNumbers,   // <— add this
            dateShipped: po.dateShipped ?? null,
            shippingCostCents: po.shippingCostCents ?? null,
            shippingCost: po.shippingCost ?? (
              po.shippingCostCents != null ? po.shippingCostCents / 100 : null
            ),
            pdfs: Array.isArray(po.pdfs) ? po.pdfs : [],
            artworkFiles: Array.isArray(po.artworkFiles) ? po.artworkFiles : [],
            purchaseOrderType: po.purchaseOrderType,
            supplierInfo: poSI,
            routingDisplay: resolvePoRoutingDisplay({ ...po, supplierInfo: poSI }),
            proofs: Array.isArray(po.proofs) ? po.proofs : [],
            resolvedRecipients: rr,
            inhouseProfile,
            _poRaw: po,
          };
      
          // --- SERVICE-ONLY detection w/ fallback ---
          const serviceCharges = gatherServiceCharges(po);

          // Helper: find a tax rate (as a decimal) on the PO if present
          const extractTaxRate = (po) => {
            const t = po?.tax || po?.defaultTax || po?.salesOrder?.tax;
            if (t == null) return 0.05;             // 👈 match Footer’s 5% fallback
            if (typeof t === 'number') return t > 1 ? t / 100 : t;
            if (typeof t === 'object') {
              const n = t.rate ?? t.taxRate ?? t.percentage ?? t.value;
              if (typeof n === 'number') return n > 1 ? n / 100 : n;
            }
            return 0.05;                             // 👈 keep the 5% fallback
          };
          

          const looksServiceOnly = (
            po.purchaseOrderType === 'Other_service_art' ||
            ((po.items?.length ?? 0) === 0 && serviceCharges.length > 0)
          );

          if (looksServiceOnly) {
            const currency =
              poSI?.approvedLines?.[0]?.line?.CurrencyCode ||
              poSI?.CurrencyCode ||
              po?.salesOrder?.currency ||
              'CAD';

            const items = serviceCharges.map((c) => ({
              itemId:   c?.otherServiceId ?? c?.id ?? null,
              itemName: getTitle(c),
              quantity: getQty(c),        // (existing qty getter is fine for display)
              price:    getTotal(c),      // <-- your existing *sell* logic
              currency,
              imageUrl: null,
            }));

            const taxRate = extractTaxRate(po);
            const rowTotal = items.reduce((s, it) => s + toNum(it.price), 0);
            const rowTax   = rowTotal * taxRate;
            
            // cost total for service-only (net)
            const poCostTotal = sumChargeCosts(serviceCharges);
            
            // 👉 preview totals (net-cost based)
            const previewSubtotal = poCostTotal;
            const previewTax      = previewSubtotal * taxRate;
            const previewTotal    = previewSubtotal + previewTax;
            
            const shared = calculatePOFinalTotals(po);
            return withDisplayTotals({
              ...baseRow,
              items,
              computedTotal: rowTotal,
              tax: rowTax,
              computedTotalWithTax: rowTotal + rowTax,
            
              // keep for reference/debug
              poCostTotal,
            
              // 👉 what we will render in the table
              previewSubtotal,
              previewTax,
              previewTotal,
            
              isServiceOnly: true,
              sharedFinalTotal: shared.total,
              sharedSubtotal: shared.subtotal,
              sharedTax: shared.tax,
              sharedCalculationMode: shared.mode,
            });
            
          }

                
          // --- ALL OTHER POs ---
          const items = (po.items || []).map((item) => {
            const decos = Array.isArray(item.decorations)
              ? item.decorations.map(normalizeDecoForCost)
              : [];
            const safeItem = { ...item, decorations: decos };

            // existing *price* computation
            const price = itemTotalForPO(po, safeItem);

            const currency =
              item.CurrencyCode ||
              poSI?.approvedLines?.[0]?.line?.CurrencyCode ||
              poSI?.CurrencyCode ||
              po?.salesOrder?.currency ||
              "CAD";

            return {
              itemId:   item.id,
              itemName: item.Name,
              sku: item.SKU || item.sku || "",
              quantity: item.Quantity,
              price,      // sell side for preview
              currency,
              imageUrl: item.ImageUrl,
              _safeItem: safeItem, // keep for cost calc below (not displayed)
            };
          });

          // COST SIDE for non service-only:
          const isEliteName = (name = "") =>
            /elite\s+decorations|elite\s+art\s+department/i.test(name);

          const splitDecosForCost = (safeItem, poObj) => {
            const all = safeItem.decorations || [];
            const poDecoratorId = String(poObj?.supplierInfo?.decoratorId || "");
            return {
              inline:   all.filter(d => !d?.DecoratorDetails?.decoratorId),
              external: all.filter(d => String(d?.DecoratorDetails?.decoratorId) === poDecoratorId),
            };
          };

          const itemCostForPO = (poObj, safeItem) => {
            const type = poObj.purchaseOrderType;
            const { inline, external } = splitDecosForCost(safeItem, poObj);

            const decoratorPO =
              !!poObj?.supplierInfo?.decoratorId ||
              isEliteName(poObj?.supplierInfo?.supplierName) ||
              (external || []).some(d => String(d?.DecoratorDetails?.source).toLowerCase() === "elite");

            // If this PO is to the decorator, the base product isn't billed here—use the external charges cost.
                         if (decoratorPO) {
                                        const prof = matchInhouseProfile(poObj);
                                        const mode = prof?.policies?.decoratorPOCost || 'external_only';
                          
                                        const baseNet      = getItemTotalNet(safeItem);
                                        const inlineCost   = sumChargeCosts(inline);
                                        const externalCost = sumChargeCosts(external);
                          
                                        switch (mode) {
                                          case 'fold_base_and_all':
                                            // In-house fold: base  inline  external
                                            return baseNet + inlineCost + externalCost;
                                          case 'charges_only':
                                            // Awards (and any future dept wanting only decoration cost on decorator PO)
                                            return externalCost;
                                          case 'external_only':
                                          default:
                                            // 3rd party default: external charges only
                                            return externalCost;
                                        }
                           }
            // Otherwise, base item cost + any inline decoration costs,
            // following your price-side type logic.
            const baseNet = getItemTotalNet(safeItem);
            const inlineCost = sumChargeCosts(inline);
            const externalCost = sumChargeCosts(external);

            if (type === "No_decoration")                               return baseNet;
            if (type === "Same_supplier_decoration")                    return baseNet + inlineCost;
            if (type === "Different_supplier_decoration")               return externalCost;
            if (type === "Combined_supplier_and_external_decoration")   return baseNet + inlineCost + externalCost;

            // default (defensive)
            return baseNet + inlineCost + externalCost;
          };

          // PO-level cost total (sum all item costs)
          const poCostTotal = items.reduce((s, it) => s + itemCostForPO(po, it._safeItem), 0);

          // existing *sell* totals
// existing sell totals
const taxRate  = extractTaxRate(po);
const rowTotal = items.reduce((s, it) => s + toNum(it.price), 0);
const rowTax   = rowTotal * taxRate;

// 👉 preview totals (net-cost based)
const previewSubtotal = poCostTotal;
const previewTax      = previewSubtotal * taxRate;
const previewTotal    = previewSubtotal + previewTax;

const shared = calculatePOFinalTotals(po);
return withDisplayTotals({
  ...baseRow,
  items: items.map(({ _safeItem, ...rest }) => rest),
  computedTotal: rowTotal,
  tax: rowTax,
  computedTotalWithTax: rowTotal + rowTax,

  // keep for reference/debug
  poCostTotal,

  // 👉 what we will render in the table
  previewSubtotal,
  previewTax,
  previewTotal,

  isServiceOnly: false,
  sharedFinalTotal: shared.total,
  sharedSubtotal: shared.subtotal,
  sharedTax: shared.tax,
  sharedCalculationMode: shared.mode,
});


        });
    }, [purchaseOrders, deletedPoIds]);

      const handleSelect = async (e, name, index, id) => {
        let newValue;
      
        // MUI DatePicker (Dayjs) → ISO string
        if (e && typeof e === "object" && typeof e.toISOString === "function") {
          newValue = e.toISOString();
          name = "nextActionDate";
        } else if (e?.target) {
          newValue = e.target.value;
        } else {
          newValue = e;
        }
      
        const po = purchaseOrderList[index];
        if (!po || !po.poId) return;
        if (blockPoMutation(name === 'productionRep'
          ? { action: 'assign-purchasing-rep' }
          : { action: 'edit-po-field', fieldName: name })) return;

        if (
          name === "proofStatus" &&
          newValue === "Proof Complete" &&
          po.proofStatus !== "Proof Complete" &&
          po.canMarkProofComplete !== true
        ) {
          toast.error("All required proofs must be approved before Proof Complete can be selected.");
          return;
        }
      
        if (!newValue) {
          toast.warn(`No change detected for "${name}"`);
          return;
        }
      
        const payload = { [name]: newValue };
      
        try {
          setLoading(true);
      
          // 1) Persist change
          await updatePurchaseOrderDetails(id, payload);
          invalidatePurchaseOrderCache(id);

          // 2) If Next Action / Date changed → add production note with friendly text
          const isNAdate = name === "nextActionDate";
          const isNA     = name === "nextAction";
      
          if (isNA || isNAdate) {
            // figure out the final values after this change
            const finalActionRaw = isNA ? newValue : po.nextAction;           // might be "2"
            const finalAction    = findActionLabel(finalActionRaw);           // "Call Supplier"
      
            const finalDateIso   = isNAdate ? newValue : po.nextActionDate?.toISOString?.();
            const finalDateStr   = formatNextActionDate(finalDateIso);        // "Aug 30, 2025, 12:00 AM"
      
            const noteMsg = `Next Action set to: ${finalAction}. Next Action Date set to: ${finalDateStr}.`;
      
            const saved = await addProductionNoteByPO(id, {
              message: noteMsg,
              action: "production-next-action-date",
              noteBucket: "production",
              scope: "purchasing",
              source: "purchase-order",
              poId: id,
              orderId: po.orderId,
            });
            const n = saved?.note || {};

            // optimistic broadcast to live views
            broadcastProductionNote({
              id: saved?.id || `tmp-${Date.now()}`,
              action:   n.action || 'production-next-action-date', // <-- title/tag to render
              message:  n.message || noteMsg,     
              createdAt: saved?.createdAt || new Date().toISOString(),
              userFirstName: saved?.userFirstName || authState?.first_name || "",
              userLastName:  saved?.userLastName  || authState?.last_name  || "",
              userRole:      saved?.userRole      || authState?.role       || "",
              poId: saved?.poId || id,
              orderId: saved?.orderId || po.orderId,
              userName: "production-note",
              _origin: "customer",
            });
          }
      
          toast.success(`${name} updated successfully`);
          await fetchPurchaseOrders();
        } catch (err) {
          console.error(err);
          toast.error(err?.response?.data?.message || `Failed to update ${name}`);
        } finally {
          setLoading(false);
        }
      };

    // id → label ("2" → "Call Supplier")
    const findActionLabel = (id) => {
        const opt = nextActionTypeOptions.find(o => String(o.id) === String(id));
        return opt?.name || String(id || '—');
    };
    
    // ISO/Dayjs/Date → "Aug 30, 2025, 12:00 AM"
    const formatNextActionDate = (val) => {
    try {
        // supports Dayjs instance, ISO string, Date, or null
        const d =
        val?.toISOString?.()                 ? new Date(val.toISOString()) :
        typeof val === 'string'              ? new Date(val) :
        val instanceof Date                  ? val :
        val?.$d instanceof Date              ? val.$d : // Dayjs .$d
        null;

        return d ? d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
        }) : '—';
    } catch {
        return '—';
    }
    };  


    return (
        <>
            {loading && <FloatingLoader />}

            <TableContainer
                component={Paper}
                className={`${styles.tableContainer} sticky-header`}
            >
                <Table className={styles.table}>
                    <TableHead>
                        <TableRow className={styles.tableRow}>
                            <TableCell className={styles.tableHeaderCell}>Select</TableCell>
                            <TableCell className={styles.tableHeaderCell}>Image</TableCell>
                            {/* <TableCell className={styles.tableHeaderCell}>Order ID</TableCell> */}
                            <TableCell className={styles.tableHeaderCell} sx={{ width: 180, minWidth: 180 }}>
                                PO#
                            </TableCell>
                            <TableCell className={styles.tableHeaderCell}>
                                PO Details
                            </TableCell>
                            <TableCell className={styles.tableHeaderCell}>Status</TableCell>
                            <TableCell className={styles.tableHeaderCell}>
                                Purchasing Rep
                            </TableCell>
                            <TableCell className={styles.tableHeaderCell} sx={{ width: 320, minWidth: 320 }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {purchaseOrderList?.map((row, index) => {
                            console.log(
                                purchaseOrderList[index], row,
                                "dropdownValues[index]?.productionRepStatus"
                            );
                            const productNames = row.items?.map((item) => item.itemName).join(", "); // Combine product names with commas
                            const productTooltipLines = (row.items || []).map((item) => {
                                const name = String(item?.itemName || "").trim();
                                const sku = String(item?.sku || item?.SKU || "").trim();
                                if (!name) return sku ? `(${sku})` : "";
                                return sku ? `${name} (${sku})` : name;
                            }).filter(Boolean);
                            const productImages = (row.items || []).map(item => {
                                const url = buildImageUrl(item.imageUrl, "normal");
                                return url || "";
                            });                           
                            const productPrice = Number(
                              row.displayTotal ?? row.computedTotalWithTax ?? row.previewTotal ?? 0
                            ).toFixed(2);
                              const currency = row.currency || row.items?.[0]?.currency || "CAD";
                              const trackingDisplay = getTrackingDisplay(row);
                              const shippedDateDisplay = formatShippedDate(row.dateShipped);
                              const shippedCostDisplay = formatShippingCost(
                                row.shippingCostCents,
                                currency
                              );
                              const routingSummary = [row.routingDisplay?.primaryLabel, row.routingDisplay?.secondaryLabel]
                                .filter(Boolean)
                                .join(" ");
                            
                            return (
                                <TableRow key={index} className={styles.row}>
                                    <TableCell sx={{
                                        borderBottom: 'none', // Remove the border-bottom
                                    }}>
                                        <Checkbox
                                            checked={selectedProducts.includes(index)}
                                            onChange={() => handleCheckboxChange(index)}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: 180, minWidth: 180, verticalAlign: "top" }}>
                                        {row.isServiceOnly ? (
                                        <Tooltip title="Service / Art PO">
                                            <Box
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                borderRadius: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'background.paper',
                                            }}
                                            >
                                            <DesignServicesIcon sx={{fontSize:"23px"}} fontSize="medium" />
                                            </Box>
                                        </Tooltip>
                                        ) : (
                                        productImages?.map((imageUrl, imgIndex) => (
                                            <img
                                            key={imgIndex}
                                            src={imageUrl}
                                            alt={imageUrl}
                                            className={styles.productImage}
                                            />
                                        ))
                                        )}
                                    </TableCell>
                                    {/* <TableCell>{row?.orderId + '-' + row?.poId}</TableCell> */}
                                    {//JSON.stringify(row)
                                    }
                                    <TableCell>
                                        {(() => {
                                          const displayPdf = pickDisplayPdf(row.pdfs);
                                          return (
                                            <>
                                              <PurchaseOrderNumberCell
                                                poNumber={row.poNumber}
                                                pdfUrl={displayPdf?.url || ""}
                                                showPoUrl={showPoUrl}
                                                pageUrl={poProofsPageUrl}
                                              />
                                            <div style={{ flex: "wrap", marginTop: "5px", width: "100%", display: "flex", gap: 8, alignItems: "center" }}>
                                            <a
                                                style={{ textDecoration: 'none', width: "100%", color: '#444', cursor: "pointer", border: "1px solid #FAFAFA", borderRadius: "10px", padding: 0, margin: 0 }}
                                                startIcon={<HistoryIcon />}
                                                onClick={() => {
                                                  // sort: active first, then by storedAt desc
                                                  const sorted = [...(row.pdfs || [])]
                                                    .filter(p => p?.url)
                                                    .sort((a, b) => {
                                                      if (!!a.active !== !!b.active) return a.active ? -1 : 1;
                                                      const ta = new Date(a.storedAt || a.createdAt || 0).getTime();
                                                      const tb = new Date(b.storedAt || b.createdAt || 0).getTime();
                                                      return tb - ta;
                                                    });
                                                  setPdfHistoryList(sorted);
                                                  setPdfHistoryTitle(row.poNumber || `PO-${row.poId}`);
                                                  setPdfHistoryOpen(true);
                                                }}
                                              >
                                                History
                                              </a>
                                            </div>
                                            </>
                                          );
                                        })()}



                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" className={styles.suppliername}>
                                            {row.supplierName}
                                        </Typography>
                                        {routingSummary ? (
                                            <Tooltip
                                                arrow
                                                placement="top-start"
                                                title={
                                                    <Box sx={{ py: 0.25 }}>
                                                        <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                                                            Route
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: "block", mb: 0.75 }}>
                                                            {routingSummary}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                                                            Product
                                                        </Typography>
                                                        {productTooltipLines.length ? (
                                                            productTooltipLines.map((line, productIndex) => (
                                                                <Typography key={`${row.poId}-product-${productIndex}`} variant="caption" sx={{ display: "block" }}>
                                                                    {line}
                                                                </Typography>
                                                            ))
                                                        ) : (
                                                            <Typography variant="caption" sx={{ display: "block", mb: 0.75 }}>
                                                                N/A
                                                            </Typography>
                                                        )}
                                                        {productTooltipLines.length ? <Box sx={{ mb: 0.75 }} /> : null}
                                                        <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                                                            Cost
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: "block" }}>
                                                            {currency} ${productPrice}
                                                        </Typography>
                                                    </Box>
                                                }
                                            >
                                                <Typography
                                                    variant="body2"
                                                    className={styles.clientDetails}
                                                    sx={{
                                                        display: "block",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: 320,
                                                        cursor: "help",
                                                    }}
                                                >
                                                    {routingSummary}
                                                </Typography>
                                            </Tooltip>
                                        ) : null}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                                            <Tooltip title="Shipping Label" arrow>
                                                <IconButton
                                                    component="a"
                                                    href={`/instance/${instanceUniqueId}/purchase-orders/${row.poId}/print/shipping-label`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    aria-label="Shipping Label"
                                                    sx={{
                                                        color: "text.secondary",
                                                        "&:hover": { color: "primary.main", bgcolor: "rgba(199,9,9,0.06)" }
                                                    }}
                                                >
                                                    <LocalShippingOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Packing Slip" arrow>
                                                <IconButton
                                                    component="a"
                                                    href={`/instance/${instanceUniqueId}/purchase-orders/${row.poId}/print/packing-slip`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    aria-label="Packing Slip"
                                                    sx={{
                                                        color: "text.secondary",
                                                        "&:hover": { color: "primary.main", bgcolor: "rgba(199,9,9,0.06)" }
                                                    }}
                                                >
                                                    <DescriptionOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {trackingDisplay ? (
                                            <Box sx={{ display: "block", mt: 0.5 }}>
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    Tracking:
                                                </Typography>{" "}
                                                <Typography component="span" variant="caption" sx={{ fontFamily: "monospace", color: "text.primary" }}>
                                                    {trackingDisplay}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                        {shippedDateDisplay ? (
                                            <Box sx={{ display: "block", mt: 0.5 }}>
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    Shipping:
                                                </Typography>{" "}
                                                <Typography component="span" variant="caption" sx={{ color: "text.primary" }}>
                                                    {shippedDateDisplay}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                        {shippedCostDisplay ? (
                                            <Box sx={{ display: "block", mt: 0.5 }}>
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    Shipping:
                                                </Typography>{" "}
                                                <Typography component="span" variant="caption" sx={{ color: "text.primary" }}>
                                                    {shippedCostDisplay}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                    </TableCell>
                                    <TableCell sx={{
                                        borderBottom: 'none', // Remove the border-bottom
                                    }}>
                                        <Tooltip
                                            title={ hoveredSupplierStatus && hoveredSupplierStatusRow === index ? (getWorkflowTooltip("PO", hoveredSupplierStatus) || statusDescriptions[hoveredSupplierStatus]) : "" }    
                                            arrow
                                            placement="top"
                                            open={ Boolean(hoveredSupplierStatus) && hoveredSupplierStatusRow === index }
                                        >
                                            <Box className="field">
                                                <CustomDropdown
                                                    label=""
                                                    name="supplierStatus"
                                                    options={workflowPurchaseOrderStatusOptions}
                                                    handleSelect={(e, name) =>{
                                                        handleSelect(
                                                            e,
                                                            name,
                                                            index,
                                                            purchaseOrderList[index]?.poId
                                                        )
                                                } }
                                                    minWidth={"150px"}
                                                    // selectedValue={''}
                                                    selectedValue={
                                                        purchaseOrderList[index]?.supplierStatus || "Created"
                                                    }
                                                    placeholder={"supplierStatus"}
                                                    disabled={!canEditSupplierStatus}
                                                    onOptionHover={id => {
                                                        setHoveredSupplierStatus(id);
                                                        setHoveredSupplierStatusRow(index);
                                                    }}  
                                                    onMenuClose={() => {
                                                        setHoveredSupplierStatus(null);
                                                        setHoveredSupplierStatusRow(null);
                                                    }}
                                                    bgColour={ statusColors[row.supplierStatus] }
                                                />
                                            </Box>
                                        </Tooltip>
                                        <Tooltip
                                            title={ hoveredConversationStatus
                                                ? supplierConversationStatusDescriptions[hoveredConversationStatus]
                                                : "" }
                                            open={ Boolean(hoveredConversationStatus) }
                                            arrow
                                            placement="top"
                                        >
                                            <Box className="field">
                                                <CustomDropdown
                                                    label=""
                                                    name="productionRepStatus"
                                                    options={poStatusIndicatorOptions}
                                                    selectedValue={
                                                        purchaseOrderList[index]?.productionRepStatus || "OK"
                                                    }
                                                    handleSelect={(e, name) =>
                                                        handleSelect(
                                                            e,
                                                            name,
                                                            index,
                                                            purchaseOrderList[index]?.poId
                                                        )
                                                    }
                                                    minWidth={"150px"}
                                                    placeholder={"Status"}
                                                    disabled={!canEditConversationStatus}
                                                    onOptionHover={id => setHoveredConversationStatus(id)}
                                                    onMenuClose={()    => setHoveredConversationStatus(null)}
                                                    bgColour={ convoColors[row.productionRepStatus] }
                                                />
                                            </Box>
                                        </Tooltip>
                                        <Tooltip
                                            title={ hoveredProofStatus && hoveredProofStatusRow === index
                                                ? proofStatusDescriptions[hoveredProofStatus]
                                                : "" }
                                            open={ Boolean(hoveredProofStatus && hoveredProofStatusRow === index) }
                                            arrow
                                            placement="top"
                                        >
                                        <Box className="field" sx={["Approved", "Proof Complete"].includes(row.proofStatus) ? {
                                            "& .MuiSelect-select": { color: "#FFFFFF !important", fontWeight: 600 },
                                            "& .MuiSelect-icon": { color: "#FFFFFF !important" },
                                            "& .MuiSvgIcon-root": { color: "#FFFFFF !important" },
                                          } : {}}>
                                            
                                            <CustomDropdown
                                                label=""
                                                minWidth={"150px"}
                                                name="proofStatus"
                                                options={buildProofRequirementOptions(row)}
                                                handleSelect={(e, name) =>
                                                    handleSelect(
                                                        e,
                                                        name,
                                                        index,
                                                        purchaseOrderList[index]?.poId
                                                    )
                                                }
                                                selectedValue={
                                                    purchaseOrderList[index]?.proofStatus || ""
                                                }
                                                placeholder={"proof"}
                                                disabled={!canEditProofStatus}
                                                onOptionHover={id => {
                                                    setHoveredProofStatus(id);
                                                    setHoveredProofStatusRow(index);
                                                }}
                                                onMenuClose={()    => {
                                                    setHoveredProofStatus(null);
                                                    setHoveredProofStatusRow(null);
                                                }}
                                                bgColour={ proofColors[row.proofStatus] }
                                            />
                                        </Box>
                                    </Tooltip>
                                    </TableCell>
                                    <TableCell >
                                        <Box
                                            className="field"
                                            sx={{ minWidth: "150px !important" }}
                                        >
                                            <CustomDropdown
                                                label=""
                                                name="productionRep"
                                                options={productionRepOption}
                                                minWidth={"150px"}
                                                handleSelect={(e, name) =>
                                                    handleSelect(
                                                        e,
                                                        name,
                                                        index,
                                                        purchaseOrderList[index]?.poId
                                                    )
                                                }
                                                selectedValue={
                                                    purchaseOrderList[index]?.productionRep || ""
                                                }
                                                placeholder={"Production Rep"}
                                                disabled={!canAssignPurchasingRep}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        className={styles.actionCOntainer}
                                        sx={{
                                            borderBottom: 'none',
                                            width: 320,
                                            minWidth: 320,
                                            maxWidth: 320,
                                            verticalAlign: "top",
                                        }} >
                                        <Tooltip
                                            title={!row.productionRep ? "Please add a Purchasing Rep before emailing." : ""}
                                            arrow
                                        >
                                            <span>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    className={styles.actionButton}
                                                    onClick={() => openEmailDialog(purchaseOrderList[index])}
                                                    disabled={!canEmailSupplier || !row.productionRep}
                                                >
                                                    Email to Supplier
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        {/* <Button
                                            size="small"
                                            variant="outlined"
                                            className={styles.actionButton}
                                        >
                                            Mark as...
                                        </Button> */}

                                        {showPoNotesAction && (
                                          <Button
                                              size="small"
                                              variant="outlined"
                                              className={styles.actionButton}
                                              onClick={() => {
                                                  setSelectedProduct(row); // ✅ Set only the clicked item
                                                  setOpenNotesDialog(true);
                                              }}
                                          >
                                              Notes
                                          </Button>
                                        )}
                                        {
                                        //console.log(selectedProduct, "SELECTED PRODUCT COMPONETNT ??>>>>>>")
                                        }

                                        <Dialog open={pdfHistoryOpen} onClose={() => setPdfHistoryOpen(false)} maxWidth="sm" fullWidth>
                                          <DialogTitle className="create-order-dialog-title">
                                            PDF History — {pdfHistoryTitle}
                                          </DialogTitle>
                                          <DialogContent className="create-order-dialog-content ">
                                            {pdfHistoryList.length === 0 ? (
                                              <Typography variant="body2" color="text.secondary">No PDFs captured yet.</Typography>
                                            ) : (
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell>#</TableCell>
                                                    <TableCell>When</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Open</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {pdfHistoryList.map((p, idx) => {
                                                    const label = p.label || `Attachment ${idx + 1}`;
                                                    const when  = fmtTs(p.storedAt || p.createdAt);
                                                    return (
                                                      <TableRow key={idx} hover>
                                                        <TableCell>{idx + 1}</TableCell>
                                                        <TableCell>{when}</TableCell>
                                                        <TableCell>
                                                          {p.active ? (
                                                            <span style={{
                                                              padding: "2px 8px",
                                                              borderRadius: 12,
                                                              background: "#e0f7fa",
                                                              fontSize: 12
                                                            }}>Active</span>
                                                          ) : (
                                                            <span style={{ fontSize: 12, color: "#777" }}>Historical</span>
                                                          )}
                                                        </TableCell>
                                                        <TableCell>
                                                          <Button
                                                            size="small"
                                                            component="a"
                                                            href={p.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{ textTransform: 'none' }}
                                                          >
                                                            {label}
                                                          </Button>
                                                        </TableCell>
                                                      </TableRow>
                                                    );
                                                  })}
                                                </TableBody>
                                              </Table>
                                            )}
                                            <Typography variant="caption" color="text.secondary" style={{ display: "block", marginTop: 8 }}>
                                              Tip: The most recent (or “active”) PDF is the one shown on the row.
                                            </Typography>
                                          </DialogContent>
                                          <DialogActions>
                                            <Button onClick={() => setPdfHistoryOpen(false)}>Close</Button>
                                          </DialogActions>
                                        </Dialog>









                                        <Dialog

                                            open={openNotesDialog}
                                            onClose={() => setOpenNotesDialog(false)}
                                            maxWidth="sm"
                                            fullWidth
                                            BackdropProps={{
                                                style: {
                                                    backgroundColor: "rgba(0, 0, 0, 0.2)", // Light dim background with low opacity
                                                },
                                            }}
                                            PaperProps={{
                                                sx: {
                                                    boxShadow: "0px 0px 2px -1px", // Remove shadow if desired
                                                },
                                            }}
                                        >
                                            <DialogTitle>Details</DialogTitle>
                                            <DialogContent>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Box>
                                                            <strong>Client</strong>
                                                            <Typography variant="body2">
                                                                {selectedProduct?.clientContactEmail}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={6}>
                                                        <Box>
                                                            <strong>Order ID</strong>
                                                            <Typography variant="body2">
                                                                {selectedProduct?.orderId}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={6}>
                                                        <Box>
                                                            <strong>Purchasing Rep</strong>
                                                            <Typography variant="body2">
                                                                {`${selectedProduct?.productionRepDetails?.first_name} ${selectedProduct?.productionRepDetails?.last_name}`}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={6}>
                                                        <Box>
                                                            <strong>Total</strong>
                                                            <Typography variant="body2">
                                                             {selectedProduct?.currency || selectedProduct?.items?.[0]?.currency || "CAD"} ${
   Number(selectedProduct?.displayTotal ?? selectedProduct?.computedTotalWithTax ?? selectedProduct?.previewTotal ?? 0).toFixed(2)
 }                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={6}>
                                                        <Box>
                                                            <strong>Supplier</strong>
                                                            <Typography variant="body2">
                                                                {selectedProduct?.supplierName}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={6}>
                                                        <Box>
                                                            <strong>Supplier Email</strong>
                                                            <Typography variant="body2">
                                                                {selectedProduct?.supplierEmail || "N/A"}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>



  <Grid item xs={4}>
    <Box>
      <strong>Tracking</strong>
      <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
        {getTrackingDisplay(selectedProduct) ? (
          <Button
            component="a"
            href={trackingUrl(getTrackingDisplay(selectedProduct))}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            {getTrackingDisplay(selectedProduct)}
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No tracking number
          </Typography>
        )}
      </Stack>
    </Box>
  </Grid>
  <Grid item xs={4}>
    <Box>
      <strong>Shipped date</strong>
      <Typography variant="body2">
        {formatShippedDate(selectedProduct?.dateShipped) || "No date"}
      </Typography>
    </Box>
  </Grid>
  <Grid item xs={4}>
    <Box>
      <strong>Shipped cost</strong>
      <Typography variant="body2">
        {formatShippingCost(
          selectedProduct?.shippingCostCents ?? (
            selectedProduct?.shippingCost != null
              ? Math.round(Number(selectedProduct.shippingCost) * 100)
              : null
          ),
          selectedProduct?.currency || selectedProduct?.items?.[0]?.currency || "CAD"
        ) || "No additional cost"}
      </Typography>
      {(selectedProduct?.shippingCostCents != null || selectedProduct?.shippingCost != null) ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "error.main" }}>
          (This is not the full shipping cost)
        </Typography>
      ) : null}
    </Box>
  </Grid>


                                                    {/*<Grid item xs={6}>
                                                        <strong>Stage</strong>{" "}
                                                        <Box className="field">
                                                            <CustomDropdown
                                                                label=""
                                                                name="supplierStatus"
                                                                options={workflowPurchaseOrderStatusOptions}
                                                                handleSelect={(e, name) =>
                                                                    handleSelect(
                                                                        e,
                                                                        name,
                                                                        index,
                                                                        purchaseOrderList[index]?.poId
                                                                    )
                                                                }
                                                                minWidth={"150px"}
                                                                // selectedValue={''}
                                                                selectedValue={
                                                                    purchaseOrderList[index]?.supplierStatus ||
                                                                    "Created"
                                                                }
                                                                placeholder={"supplierStatus"}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={2}>
                                                        <strong>Status</strong>
                                                        <Box >
                                                            <CustomDropDownColor

                                                                name="productionRepStatus"
                                                                options={poStatusIndicatorOptions}
                                                                selectedValue={
                                                                    purchaseOrderList[index]?.productionRepStatus || ""
                                                                }
                                                                handleSelect={(e, name) =>
                                                                    handleSelect(
                                                                        e,
                                                                        name,
                                                                        index,
                                                                        purchaseOrderList[index]?.poId
                                                                    )
                                                                }
                                                                minWidth={"150px"}
                                                            // selectedValue={''}

                                                            />
                                                        </Box>

                                                    </Grid>*/}
                                                    <Grid item xs={6}>
                                                        <strong>Next Action</strong>{" "}
                                                        <Box
                                                            className="field"
                                                            sx={{ minWidth: "150px !important" }}
                                                        >
                                                            <CustomDropdown
                                                                label=""
                                                                name="nextAction"
                                                                options={nextActionTypeOptions}
                                                                selectedValue={
                                                                    purchaseOrderList[index]?.nextAction || ""
                                                                }
                                                                minWidth={"150px"}
                                                                handleSelect={(e, name) =>
                                                                    handleSelect(
                                                                        e,
                                                                        name,
                                                                        index,
                                                                        purchaseOrderList[index]?.poId
                                                                    )
                                                                }

                                                                placeholder={"Next Action"}
                                                                disabled={!canEditNextAction}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <strong>Next Action Date</strong>
                                                        <Box sx={{ marginTop: 1.2 }}>
                                                            <CustomDatePicker
                                                                name="nextActionDate"
                                                                value={purchaseOrderList[index]?.nextActionDate}
                                                                onChange={(e, value) =>
                                                                    handleSelect(e, value, index, purchaseOrderList[index]?.poId)
                                                                }
                                                                disabled={!canEditNextActionDate}
                                                            />
                                                        </Box>
                                                    </Grid>

                                                </Grid>
                                            </DialogContent>

                                            <DialogActions>
                                                <Button onClick={() => setOpenNotesDialog(false)}>
                                                    Close
                                                </Button>
                                            </DialogActions>
                                        </Dialog>

                                        <Tooltip title={!canDeletePurchaseOrder ? "Only Production may delete Purchase Orders." : ""}>
                                          <span>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                className={styles.deleteButton}
                                                disabled={!canDeletePurchaseOrder}
                                                onClick={() => {
                                                    if (blockPoMutation({ action: 'delete-purchase-order' })) return;
                                                    const poId = purchaseOrderList[index]?.poId;
                                                    if (!poId) return;
                                                    const confirmed = window.confirm(
                                                        "Archive this purchase order? Its purchasing history will be retained."
                                                    );
                                                    if (!confirmed) return;
                                                    deletePurchaseOrder(poId);
                                                }}
                                            >
                                                Archive
                                            </Button>
                                          </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {isAdministrator && (
              <Box sx={{ mt: 2 }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Debug: Purchase Order Row Payloads</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      component="pre"
                      variant="caption"
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace" }}
                    >
                      {JSON.stringify({
                        purchaseOrderList,
                        totalDebug: purchaseOrderList.map((row) => ({
                          poId: row.poId,
                          poNumber: row.poNumber,
                          currency: row.currency || row.items?.[0]?.currency || "CAD",
                          displayTotal: row.displayTotal,
                          displayTotalSource: row.displayTotalSource,
                          sharedFinalTotal: row.sharedFinalTotal,
                          sharedSubtotal: row.sharedSubtotal,
                          sharedTax: row.sharedTax,
                          sharedCalculationMode: row.sharedCalculationMode,
                          computedTotal: row.computedTotal,
                          tax: row.tax,
                          computedTotalWithTax: row.computedTotalWithTax,
                          previewSubtotal: row.previewSubtotal,
                          previewTax: row.previewTax,
                          previewTotal: row.previewTotal,
                          poCostTotal: row.poCostTotal,
                        })),
                      }, null, 2)}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}



            {emailOpen && (
                <EmailSupplierDialog
                    open={emailOpen}
                    row={emailRow}
                    currentUserEmail={authState?.email}
                    instanceUniqueId={instanceUniqueId}
                    showPoUrl={showPoUrl}
                    templates={emailTemplates}
                    onClose={async (didSend) => {
                    setEmailOpen(false);
                    setEmailRow(null);
                    if (didSend) {
                        // reflect Submitted / lastEmailedAt changes, etc.
                        await fetchPurchaseOrders();
                    }
                    }}
                />
                )}

        </>
    );
};











const ADDRESS_SPLIT_RX = /[;,]+|\r?\n+/; // commas, semicolons, or new lines (NOT spaces)
const EMAIL_ONLY_RX    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const normalizeEmailToken = (s = "") => {
  const trimmed = String(s).trim();
  if (!trimmed) return "";
  const angleMatch = trimmed.match(/<([^>]+)>/);
  const raw = (angleMatch ? angleMatch[1] : trimmed).replace(/^mailto:/i, "").trim();
  return raw;
};

const splitAndExtractEmails = (v = "") =>
  String(v)
    .split(ADDRESS_SPLIT_RX) // do NOT split on spaces
    .map(normalizeEmailToken)
    .filter(Boolean);

const isEmail = (v = "") => EMAIL_ONLY_RX.test(String(v).trim());

const resolveSupplierIdFromPo = (row = {}) =>
  row?.supplierInfo?.supplierId ??
  row?.coreSupplierId ??
  row?.supplierId ??
  row?.supplierInfo?.Id ??
  row?.supplierInfo?.id ??
  row?.supplier?.Id ??
  row?.supplier?.id ??
  null;

const parseSupplierReferences = (references) => {
  if (Array.isArray(references)) return references;
  if (typeof references !== "string") return [];
  try {
    const parsed = JSON.parse(references);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const hasCustomCoreReference = (references) =>
  parseSupplierReferences(references).some((reference) => reference?.source === "custom-core");

const sanitizeSupplierContacts = (contacts = []) => {
  const seen = new Set();

  return contacts.reduce((list, contact) => {
    const email = normalizeEmailToken(contact?.email || "").toLowerCase();
    if (!isEmail(email) || seen.has(email)) return list;

    seen.add(email);
    list.push({
      ...contact,
      email,
      firstName: String(contact?.firstName || "").trim(),
      lastName: String(contact?.lastName || "").trim(),
      title: String(contact?.title || "").trim(),
    });
    return list;
  }, []);
};

const formatSupplierContactLabel = (contact = {}) => {
  const fullName = [contact?.firstName, contact?.lastName].filter(Boolean).join(" ").trim();
  if (fullName && contact?.title) return `${fullName} (${contact.title})`;
  if (fullName) return fullName;
  if (contact?.title) return contact.title;
  return contact?.email || "Supplier contact";
};

const EmailSupplierDialog = ({ open, onClose, row, currentUserEmail, templates = [], instanceUniqueId, showPoUrl = false }) => {
  const normalizedEmailState = useMemo(() => recipientFieldsFromPurchaseOrder(row), [row]);
  const normalizedRow = normalizedEmailState.row;
  const rr = normalizedRow.resolvedRecipients;

  // PO + sender context
  const supplierEmail = normalizedRow.supplierEmail || "";
  const poNumber = row?.poNumber || `PO-${row?.poId || ""}`;
  const poEmailSubject = buildPurchaseOrderSupplierEmailSubject({ poNumber, poId: row?.poId });

  const fromUserId       = normalizedRow?.productionRepDetails?.id || normalizedRow?.productionRep || null; // backend picks sender
  const fromEmailDisplay =
    normalizedRow?.productionRepDetails?.email ||
    process.env.REACT_APP_DEFAULT_FROM_EMAIL ||
    "no-reply@yourdomain";

// PDF preview dialog state (shared for “magnifying glass”)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc]   = useState('');
  const [previewName, setPreviewName] = useState('');
  const openPreview = (src, name) => {
    setPreviewSrc(src || '');
    setPreviewName(name || (src ? src.split('/').pop() : ''));
    setPreviewOpen(true);
  };

  // tabs
  const [tab, setTab] = useState("live"); // 'live' | 'test'

  // recipients (Live)
  const [toField,  setToField]  = useState(supplierEmail);
  const [ccField,  setCcField]  = useState("");
  const [bccField, setBccField] = useState("");

  // Test recipient (Test tab)
  const [testTo, setTestTo] = useState(currentUserEmail || "");

  // email content
  const [emailComposerValue, setEmailComposerValue] = useState({
    to: [],
    cc: [],
    bcc: [],
    subject: "",
    body: "",
    templateId: "",
    layout: "elite",
    quickActions: {},
  });

  // attachments from row
  const [pdfs, setPdfs] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [sending, setSending] = useState(false);
  const [supplierRevisionPreview, setSupplierRevisionPreview] = useState(null);
  const subjectEditable = supplierRevisionPreview?.subjectEditable === true;
  const subjectManuallyEditedRef = useRef(false);
  useEffect(() => {
    if (open) subjectManuallyEditedRef.current = false;
    // Reset only when switching PO or reopening, not on routine row refresh.
    setSupplierRevisionPreview(null);
  }, [open, row?.poId]);
  const selectedSupplierPdfUrls = pdfs.filter((item) => item?.active && item?.url).map((item) => item.url).sort().join('|');
  const [supplierRevisionLoading, setSupplierRevisionLoading] = useState(false);
  useEffect(() => {
    if (!open || !row?.poId) return;
    let cancelled = false;
    setSupplierRevisionLoading(true);
    axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/purchase-order/${row.poId}/supplier-send-history`, {
      params: { pdfUrls: JSON.stringify(selectedSupplierPdfUrls ? selectedSupplierPdfUrls.split("|") : []) },
      headers: { accessToken: localStorage.getItem("accessToken") },
    }).then(({ data }) => {
      if (cancelled) return;
      setSupplierRevisionPreview(data);
      setEmailComposerValue((previous) => ({
        ...previous,
        // Use the last recorded subject as the starting point for manual POs.
        // Never replace a user's edits when PDF selection refreshes the preview.
        subject: data.subjectEditable && subjectManuallyEditedRef.current
          ? previous.subject : data.subject,
      }));
    }).catch((error) => {
      if (!cancelled) {
        console.warn("PO supplier send preview unavailable", error?.message);
        toast.error("Could not verify supplier revision. Sending is unavailable.");
        setSupplierRevisionPreview(null);
      }
    }).finally(() => { if (!cancelled) setSupplierRevisionLoading(false); });
    return () => { cancelled = true; };
  }, [open, row?.poId, selectedSupplierPdfUrls]);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [manualRecipientConfirmed, setManualRecipientConfirmed] = useState(false);
  const [supplierContacts, setSupplierContacts] = useState([]);
  const [supplierContactsLoading, setSupplierContactsLoading] = useState(false);
  const [supplierContactsError, setSupplierContactsError] = useState("");
  const [supplierContactTarget, setSupplierContactTarget] = useState("to");
  const [supplierContactQuery, setSupplierContactQuery] = useState("");
  const [preferredPoEmail, setPreferredPoEmail] = useState("");
  const [preferredPoEmailLoading, setPreferredPoEmailLoading] = useState(false);
  const manuallyEditedToRef = useRef(false);
  const recipientInitializationKey = `${normalizedRow?.poId || normalizedRow?.id || ""}:live:${JSON.stringify(rr)}`;
  const initializedRecipientKeyRef = useRef("");

  const resolvedSupplierId = useMemo(() => resolveSupplierIdFromPo(row), [row]);
  const supplierReferences = useMemo(
    () =>
      row?.supplierReferences ??
      row?.supplierInfo?.references ??
      row?.supplierInfo?.References ??
      row?.supplier?.References ??
      row?.References ??
      [],
    [row]
  );
  const isCustomCoreSupplier = useMemo(
    () => hasCustomCoreReference(supplierReferences),
    [supplierReferences]
  );

  // seed on open
  useEffect(() => {
    if (!open) return;
  
    setTab("live");
    const defaultBody = showPoUrl
      ? `Hello,<br/><br/>Please find the purchase order attached for ${poNumber}.<br/>Click the button (or link) to view the live PO. Thank you!`
      : `Hello,<br/><br/>Please find the purchase order attached for ${poNumber}.<br/>Please view the attached PDF instead. Thank you!`;
    setEmailComposerValue((prev) => ({
      ...prev,
      // Row refreshes (including a PDF regeneration) must not erase
      // a subject typed for an already-open legacy PO.
      subject: subjectManuallyEditedRef.current ? prev.subject : poEmailSubject,
      body: defaultBody,
      templateId: "",
      layout: "elite",
    }));
  
    if (initializedRecipientKeyRef.current !== recipientInitializationKey) {
      manuallyEditedToRef.current = false;
      setPreferredPoEmail("");
      setToField(normalizedEmailState.to);
      setCcField(normalizedEmailState.cc);
      setBccField(normalizedEmailState.bcc);
      initializedRecipientKeyRef.current = recipientInitializationKey;
    }
  
    setTestTo(currentUserEmail || "");
    setManualRecipientConfirmed(false);
  
    setPdfs(Array.isArray(row?.pdfs) ? row.pdfs.map(p => ({ ...p })) : []);
    setArtworks(seedArtworkAttachmentSelection(row?.artworkFiles, row));
  
    // helpful open-time audit
    console.info("PO email routing (dialog opened)", {
      poId: row?.poId,
      poNumber,
      supplierName: row?.supplierName || "",
      reason: rr.reason || "default",
      resolved: rr
    });
  }, [open, poNumber, poEmailSubject, currentUserEmail, fromEmailDisplay, normalizedRow, normalizedEmailState, recipientInitializationKey, rr, row, showPoUrl]);

  useEffect(() => {
    if (!open) initializedRecipientKeyRef.current = "";
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSupplierContacts([]);
      setSupplierContactsLoading(false);
      setSupplierContactsError("");
      return;
    }

    if (!resolvedSupplierId || !instanceUniqueId) {
      setSupplierContacts([]);
      setSupplierContactsLoading(false);
      setSupplierContactsError("");
      return;
    }

    let cancelled = false;

    const loadSupplierContacts = async () => {
      try {
        setSupplierContactsLoading(true);
        setSupplierContactsError("");

        const { data } = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/suppliers/${resolvedSupplierId}/contacts`,
          {
            params: { instanceUniqueId },
            headers: { accessToken: localStorage.getItem("accessToken") },
          }
        );

        if (cancelled) return;
        setSupplierContacts(sanitizeSupplierContacts(data?.contacts));
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load supplier contacts for PO email", error);
        setSupplierContacts([]);
        setSupplierContactsError("Supplier contacts couldn't be loaded.");
      } finally {
        if (!cancelled) setSupplierContactsLoading(false);
      }
    };

    loadSupplierContacts();

    return () => {
      cancelled = true;
    };
  }, [open, resolvedSupplierId, instanceUniqueId]);
  

  // Only replace the standard single supplier default. Never overwrite manually
  // edited To addresses, special routing, or legacy multi-recipient snapshots.
  useEffect(() => {
    if (!open || !resolvedSupplierId || !instanceUniqueId) {
      setPreferredPoEmailLoading(false);
      return;
    }
    const originalTo = splitAndExtractEmails(normalizedEmailState.to);
    const baseEmail = normalizeEmailToken(supplierEmail).toLowerCase();
    if (originalTo.length !== 1 || originalTo[0].toLowerCase() !== baseEmail ||
      isDecoratorPo(normalizedRow)) {
      setPreferredPoEmailLoading(false);
      return;
    }
    let active = true;
    setPreferredPoEmailLoading(true);
    axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/suppliers/${resolvedSupplierId}/po-email-preference`, {
      params: { instanceUniqueId },
      headers: { accessToken: localStorage.getItem('accessToken') },
    }).then(({ data }) => {
      if (!active || data?.mode !== 'contact' || !isEmail(data?.contactEmail)) return;
      const preferred = normalizeEmailToken(data.contactEmail).toLowerCase();
      setPreferredPoEmail(preferred);
      if (!manuallyEditedToRef.current) setToField(preferred);
    }).catch(() => {
      // Non-disruptive fallback: legacy PO recipient remains unchanged.
    }).finally(() => { if (active) setPreferredPoEmailLoading(false); });
    return () => { active = false; };
  }, [open, resolvedSupplierId, instanceUniqueId, normalizedEmailState.to, supplierEmail, normalizedRow]);

  // compute clean recipient arrays
  const toListRaw  = useMemo(() => splitAndExtractEmails(toField), [toField]);
  const ccListRaw  = useMemo(() => splitAndExtractEmails(ccField), [ccField]);
  const bccListRaw = useMemo(() => splitAndExtractEmails(bccField), [bccField]);

  // only validate To or Test; Cc/Bcc never red
  const toList  = useMemo(() => toListRaw.filter(isEmail), [toListRaw]);
  const ccList  = useMemo(() => ccListRaw.filter(isEmail), [ccListRaw]);
  const bccList = useMemo(() => bccListRaw.filter(isEmail), [bccListRaw]);
  const selectedSupplierContactEmails = useMemo(
    () => new Set((supplierContactTarget === "cc" ? ccListRaw :
      supplierContactTarget === "bcc" ? bccListRaw : toListRaw)
      .map((email) => email.toLowerCase())),
    [supplierContactTarget, toListRaw, ccListRaw, bccListRaw]
  );

  const toValid   = toList.length > 0;
  const testValid = isEmail(testTo);

  const automaticTo = useMemo(() => new Set([...rr.to, ...(preferredPoEmail ? [preferredPoEmail] : [])]), [rr.to, preferredPoEmail]);
  const automaticCc = useMemo(() => new Set(rr.cc), [rr.cc]);
  const automaticBcc = useMemo(() => new Set(rr.bcc), [rr.bcc]);
  const hasManualRecipient = toList.some((email) => !automaticTo.has(email.toLowerCase())) ||
    ccList.some((email) => !automaticCc.has(email.toLowerCase())) ||
    bccList.some((email) => !automaticBcc.has(email.toLowerCase()));
  const canSend = !sending && !supplierRevisionLoading && !!supplierRevisionPreview && !!emailComposerValue.subject?.trim() && (tab === "live" ? (!preferredPoEmailLoading && toValid && (!hasManualRecipient || manualRecipientConfirmed)) : testValid);
  const poPreviewUrl = `${(process.env.REACT_APP_NODEJS_CLIENT_URL || window.location.origin).replace(/\/+$/, "")}/generated-purchase-order/${normalizedRow.poId}`;
  const emailPreviewBody = emailComposerValue.layout === "elite"
    ? `${emailComposerValue.body || ""}${
        showPoUrl
          ? `<div style="text-align:center;margin:28px 0 8px;"><a href="${poPreviewUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:16px 28px;border-radius:999px;background:#D43131;color:#FFFFFF;text-decoration:none;font-weight:700;">View Purchase Order</a></div><div style="text-align:center;margin:4px 0 0;"><a href="${poPreviewUrl}" target="_blank" rel="noopener noreferrer" style="color:#1A73E8;text-decoration:underline;">If the button does not display properly, click here</a></div>`
          : (emailComposerValue.body || "").toLowerCase().includes("attached pdf")
            ? ""
            : "<p>Please view the attached PDF instead.</p>"
      }`
    : emailComposerValue.body;

  const togglePdf = (idx) => setPdfs(prev => prev.map((p, i) => (i === idx ? { ...p, active: !p.active } : p)));
  const checkAll  = (val) => setPdfs(prev => prev.map(p => ({ ...p, active: !!val })));
  const toggleArtwork = (idx) => setArtworks(prev => prev.map((a, i) => (i === idx ? { ...a, active: !a.active } : a)));
  const checkAllArt   = (val) => setArtworks(prev => prev.map(a => ({ ...a, active: !!val })));
  const toggleSupplierContact = (contactEmail) => {
    const normalizedTarget = normalizeEmailToken(contactEmail || "").toLowerCase();
    if (!isEmail(normalizedTarget)) return;
    if (supplierContactTarget === "to") manuallyEditedToRef.current = true;
    // Keep all existing recipient fields and their manual confirmation rules.
    const updateField = supplierContactTarget === "cc" ? setCcField :
      supplierContactTarget === "bcc" ? setBccField : setToField;
    updateField((currentValue) => {
      const tokens = splitAndExtractEmails(currentValue);
      const existingEmails = new Set();
      const dedupedTokens = [];

      tokens.forEach((token) => {
        const normalized = token.toLowerCase();
        if (existingEmails.has(normalized)) return;
        existingEmails.add(normalized);
        dedupedTokens.push(token);
      });

      if (existingEmails.has(normalizedTarget)) {
        return dedupedTokens
          .filter((token) => token.toLowerCase() !== normalizedTarget)
          .join(", ");
      }

      return [...dedupedTokens, normalizedTarget].join(", ");
    });
  };

  const handleSend = async () => {
    if (!canSend || supplierRevisionLoading || !supplierRevisionPreview) return;
    try {
      setSending(true);

      const pdfsOverride = pdfs
        .filter(p => p?.url && p.active)
        .map((p, i) => ({ url: p.url, name: p.label || `Attachment ${i + 1}.pdf` }));

      const artworksOverride = artworks
          .filter(a => a?.url && a.active)
          .map((a, i) => ({ url: a.url, name: a.name || `artwork-${i + 1}.jpg` }));

      const base = {
        subject: emailComposerValue.subject,
        fromUserId,
        pdfsOverride,
        artworksOverride,
        useTemplate: emailComposerValue.layout === "elite",
        message: emailComposerValue.body,
        initiatorEmail: currentUserEmail || "",
      };

      const payload = tab === "test"
        ? { ...base, simulate: true, testTo: String(testTo).trim() }
        : { ...base, simulate: false, to: toList, cc: ccList, bcc: bccList, manualRecipientConfirmed };

      await sendMailToSupplier(normalizedRow.poId, payload);

      toast.success(tab === "test" ? "Test email sent" : "Supplier email sent");
      onClose(true);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (generatingPreview) return;

    try {
      setGeneratingPreview(true);
      const preview = await previewPurchaseOrderPdf(normalizedRow.poId);
      if (!preview?.pdfUrl) throw new Error("The preview response did not include a PDF URL.");
      openPreview(preview.pdfUrl, preview.fileName || `${poNumber}.pdf`);
    } catch (error) {
      console.error("Failed to generate Purchase Order preview", error);
      toast.error(
        error?.response?.data?.details ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate Purchase Order preview"
      );
    } finally {
      setGeneratingPreview(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle className="create-order-dialog-title">
        Send Purchase Order Email
      </DialogTitle>

      <DialogContent className="create-order-dialog-content">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab value="live" label="Live" />
          <Tab value="test" label="Test" />
        </Tabs>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.paper" }}>
          <Typography variant="body2" color="text.secondary">
            {tab === "live"
              ? "Live: send to supplier (To / Cc / Bcc). Initiator and order/client reps will be auto-BCC’d by the system."
              : "Test: sends ONLY to the test recipient below. Supplier and reps won’t be emailed."}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            <i>From: {fromEmailDisplay}</i>
          </Typography>
        </Paper>


        {rr && (
  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
    <Typography variant="subtitle2">Routing</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      Reason: {rr.reason || 'default'}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: .5 }}>
     Auto To: {rr.to.join(', ') || '—'}
    </Typography>
    {Boolean(rr.cc?.length) && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Auto Cc: {rr.cc.join(', ')}
      </Typography>
    )}
    {Boolean(rr.bcc?.length) && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Auto Bcc: {rr.bcc.join(', ')}
      </Typography>
    )}
  </Paper>
    )}



        <Stack spacing={2}>
          <Box>
            {tab === "live" ? (
              <Stack spacing={1.5}>
                {(supplierContactsLoading || supplierContacts.length > 0 || supplierContactsError) && (
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default" }}>
                    <Typography variant="subtitle2" sx={{ mb: supplierContacts.length > 0 ? 1 : 0.5 }}>
                      Supplier contacts
                    </Typography>
                    {supplierContactsLoading ? (
                      <Typography variant="caption" color="text.secondary">
                        Loading saved supplier contacts...
                      </Typography>
                    ) : supplierContacts.length > 0 ? (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Choose the recipient field, then click a contact. Additional contacts are never added automatically.
                        </Typography>
                        <Tabs value={supplierContactTarget} onChange={(_, value) => setSupplierContactTarget(value)}
                          variant="fullWidth" sx={{ minHeight: 34, mb: 1 }}>
                          <Tab value="to" label="To" sx={{ minHeight: 34 }} />
                          <Tab value="cc" label="CC" sx={{ minHeight: 34 }} />
                          <Tab value="bcc" label="BCC" sx={{ minHeight: 34 }} />
                        </Tabs>
                        <TextField size="small" fullWidth
                          label="Search supplier contacts" value={supplierContactQuery}
                          onChange={event => setSupplierContactQuery(event.target.value)}
                          placeholder="Name, department, title or email" sx={{ mb: 1 }} />
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {supplierContacts.filter(contact =>
                            [contact.firstName,contact.lastName,contact.title,contact.department,contact.email]
                              .join(" ").toLowerCase().includes(supplierContactQuery.trim().toLowerCase())
                          ).map((contact) => {
                            const email = contact.email.toLowerCase();
                            const selected = selectedSupplierContactEmails.has(email);
                            return (
                              <Chip
                                key={email}
                                label={`${formatSupplierContactLabel(contact)} - ${contact.email}${contact.source === "linked-custom" ? " · Elite Core linked" : ""}`}
                                onClick={() => toggleSupplierContact(contact.email)}
                                color={selected ? "primary" : "default"}
                                variant={selected ? "filled" : "outlined"}
                                clickable
                              />
                            );
                          })}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Click to add or remove a contact in {supplierContactTarget.toUpperCase()}.
                          Existing supplier email and manually entered recipients remain editable.
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {supplierContactsError || "No saved supplier contacts are available for this supplier."}
                      </Typography>
                    )}
                    {!isCustomCoreSupplier && !supplierContactsLoading && supplierContacts.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        Loaded using the PO&apos;s exact supplier ID.
                      </Typography>
                    )}
                  </Paper>
                )}
                <TextField
                  label="To"
                  value={toField}
                  onChange={(e) => { manuallyEditedToRef.current = true; setToField(e.target.value); }}
                  error={!toValid}
                  helperText={!toValid ? "Enter at least one valid email" : "Separate multiple with commas, semicolons, or new lines."}
                  fullWidth
                />
                {preferredPoEmailLoading && (
                  <Typography variant="caption" color="text.secondary">Checking this instance's preferred supplier recipient…</Typography>
                )}
                {preferredPoEmail && (
                  <Typography variant="caption" color="text.secondary">
                    Instance preferred PO contact suggested in To. The original supplier email remains available.
                  </Typography>
                )}
                {hasManualRecipient && (
                  <FormControlLabel
                    control={<Checkbox checked={manualRecipientConfirmed} onChange={(e) => setManualRecipientConfirmed(e.target.checked)} />}
                    label="I confirm these manually entered To/Cc/Bcc recipients are intentional"
                  />
                )}
                <TextField
                  label="Cc (optional)"
                  value={ccField}
                  onChange={(e) => setCcField(e.target.value)}
                  //helperText={"Optional. Invalid emails will be ignored."}
                  fullWidth
                />
                <TextField
                  label="Bcc (optional)"
                  value={bccField}
                  onChange={(e) => setBccField(e.target.value)}
                  //helperText={"Optional. Invalid emails will be ignored."}
                  fullWidth
                />
              </Stack>
            ) : (
              <TextField
                label="Test recipient"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                error={!testValid}
                helperText={testValid ? "Only this address will receive the test." : "Enter a valid email"}
                fullWidth
              />
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Content</Typography>
            <EmailComposer
              contacts={[]}
              templates={templates}
              value={emailComposerValue}
              onChange={(nextValue) => {
                if (subjectEditable && nextValue?.subject !== emailComposerValue.subject) {
                  subjectManuallyEditedRef.current = true;
                }
                setEmailComposerValue(nextValue);
              }}
              showRecipients={false}
              showAttachments={false}
              showQuickActions={false}
              subjectReadOnly={!subjectEditable}
              previewBodyHtml={emailPreviewBody}
            />
            {subjectEditable && (
              <Typography variant="caption" color="text.secondary">
                Manual PO: enter the correct revision manually. The last recorded subject is
                prefilled when available; send tracking will not renumber it.
              </Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Attach PDF(s){" "}
              <Typography component="span" variant="caption" color="text.secondary">
                ({pdfs.filter(p => p.active).length} selected of {pdfs.length})
              </Typography>
            </Typography>

            {pdfs.length > 0 ? (
              <>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button size="small" onClick={() => checkAll(true)}>Check all</Button>
                  <Button size="small" onClick={() => checkAll(false)}>Uncheck all</Button>
                </Stack>
                <Stack spacing={1}>
                {pdfs.map((p, i) => {
                  const label = p.label || `Attachment ${i + 1}`;
                  const canPreview = isPdfUrl(p.url);
                  return (
                    <Stack
                      key={i}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                    >
                      <FormControlLabel
                        control={<Checkbox checked={!!p.active} onChange={() => togglePdf(i)} />}
                        label={
                          <a href={p.url} target="_blank" rel="noopener noreferrer">
                            {label}
                          </a>
                        }
                      />
                      {canPreview && (
                        <IconButton
                          size="small"
                          aria-label="Preview PDF"
                          onClick={() => openPreview(p.url, label)}
                          title="Preview"
                          sx={{ ml: -1 }} // tucks icon closer to label
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No PDFs captured yet. The email can still include the live PO link via the template.
              </Typography>
            )}
          </Box>

          <Divider />


          <Box>
           <Typography variant="subtitle2" sx={{ mb: 1 }}>
             Attach Artwork{" "}
             <Typography component="span" variant="caption" color="text.secondary">
               ({artworks.filter(a => a.active).length} selected of {artworks.length})
             </Typography>
           </Typography>
 
           {artworks.length > 0 ? (
             <>
               <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                 <Button size="small" onClick={() => checkAllArt(true)}>Check all</Button>
                 <Button size="small" onClick={() => checkAllArt(false)}>Uncheck all</Button>
               </Stack>
               <Stack spacing={1}>
                 {artworks.map((a, i) => (
                   <FormControlLabel
                     key={i}
                     control={<Checkbox checked={!!a.active} onChange={() => toggleArtwork(i)} />}
                     label={
                       <a href={a.url} target="_blank" rel="noopener noreferrer">
                         {a.name || `Artwork ${i + 1}`}
                       </a>
                     }
                   />
                 ))}
               </Stack>
             </>
           ) : (
             <Typography variant="caption" color="text.secondary">
               No artwork files captured for this PO.
             </Typography>
           )}
         </Box>


        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={sending || generatingPreview}>Cancel</Button>
        <Button
          variant="outlined"
          startIcon={generatingPreview ? <CircularProgress size={18} color="inherit" /> : <VisibilityOutlinedIcon />}
          onClick={handleGeneratePreview}
          disabled={generatingPreview}
        >
          {generatingPreview ? "Generating Preview..." : "Preview PDF"}
        </Button>
        <Button variant="contained" onClick={handleSend} disabled={!canSend}>
          {tab === "test" ? "Send Test" : "Send"}
        </Button>
      </DialogActions>

      {/* One global PDF dialog for this popup */}
      {isPdfUrl(previewSrc) && (
        <PdfViewerDialog
          open={previewOpen}
          src={previewSrc}
          fileName={previewName}
          onClose={() => setPreviewOpen(false)}
        />
      )}


    </Dialog>
  );
};
  
export default SelectedProductsPopup;
